import styled from 'styled-components';

// Layout: header bar + three columns (sources | pages | output). The
// palette and Manrope face match the merge modal / MergeSelectedBar so the
// builder reads as part of the same document tooling family.

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f7f9fc;
  font-family: 'Manrope', sans-serif;
  color: #1b2028;
`;

export const HeaderBar = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  background: #ffffff;
  border-bottom: 1px solid #dde2e9;
  flex-shrink: 0;
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  color: #48566b;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #f1f6fe;
  }
`;

export const HeaderTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
`;

export const HeaderSpacer = styled.div`
  flex: 1;
`;

export const OutputNameInput = styled.input`
  width: 320px;
  padding: 7px 10px;
  border: 1px solid #c9d2de;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  color: #1b2028;

  &:focus {
    outline: none;
    border-color: #0c53bb;
  }
`;

export const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 6px;
  background: #0c53bb;
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    background: #aeb8c4;
    cursor: not-allowed;
  }
`;

export const Columns = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

export const Column = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid #dde2e9;
  background: ${(p) => (p.$tinted ? '#fbfcfe' : '#ffffff')};

  &:last-child {
    border-right: none;
  }
`;

export const SourcesColumn = styled(Column)`
  width: 280px;
  flex-shrink: 0;
`;

export const PagesColumn = styled(Column)`
  flex: 1;
`;

export const OutputColumn = styled(Column)`
  width: 360px;
  flex-shrink: 0;
`;

export const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #eef1f6;
  font-size: 13px;
  font-weight: 700;
  color: #48566b;
  flex-shrink: 0;
`;

export const ColumnBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  min-height: 0;
`;

export const AddDocumentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px dashed #9db8de;
  border-radius: 6px;
  background: #f7faff;
  color: #0c53bb;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 12px;
  cursor: pointer;
  width: 100%;
  justify-content: center;

  &:hover {
    background: #eef5ff;
  }
`;

export const SourceCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid ${(p) => (p.$active ? '#0c53bb' : '#dde2e9')};
  background: ${(p) => (p.$active ? '#f1f6fe' : '#ffffff')};
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
`;

export const SourceCardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SourceCardName = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SourceCardMeta = styled.div`
  font-size: 11px;
  color: #8a97a8;
  margin-top: 2px;
`;

export const SourceCardAction = styled.button`
  border: none;
  background: none;
  color: #0c53bb;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  white-space: nowrap;

  &:hover {
    background: #e3eefd;
  }
`;

export const PagesToolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #eef1f6;
  flex-shrink: 0;
  font-size: 12px;
  color: #48566b;
`;

export const RangeInput = styled.input`
  width: 220px;
  padding: 6px 10px;
  border: 1px solid #c9d2de;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: #0c53bb;
  }
`;

export const ToolbarButton = styled.button`
  border: 1px solid #c9d2de;
  border-radius: 6px;
  background: #ffffff;
  color: #48566b;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f1f6fe;
  }

  &:disabled {
    color: #aeb8c4;
    cursor: not-allowed;
  }
`;

export const PrimaryToolbarButton = styled(ToolbarButton)`
  background: #0c53bb;
  border-color: #0c53bb;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #0a47a1;
  }

  &:disabled {
    background: #aeb8c4;
    border-color: #aeb8c4;
    color: #ffffff;
  }
`;

export const ToolbarError = styled.span`
  color: #c0392b;
  font-size: 12px;
`;

export const ThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(p) => (p.$thumbWidth || 120) + 12}px, 1fr)
  );
  gap: 12px;
`;

export const ThumbCard = styled.div`
  position: relative;
  border: 2px solid ${(p) => (p.$selected ? '#0c53bb' : '#dde2e9')};
  background: ${(p) => (p.$selected ? '#f1f6fe' : '#ffffff')};
  border-radius: 6px;
  padding: 6px;
  cursor: grab;
  user-select: none;
  text-align: center;

  &:hover {
    border-color: #0c53bb;
  }

  canvas {
    display: block;
    width: 100% !important;
    height: auto !important;
    margin: 0 auto;
  }
`;

export const ThumbPageNumber = styled.div`
  font-size: 11px;
  color: #48566b;
  margin-top: 4px;
`;

export const ThumbPager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 0 2px;
  font-size: 12px;
  color: #48566b;
`;

export const EmptyPanelText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  color: #8a97a8;
  font-size: 13px;
  text-align: center;
  padding: 24px;
`;

export const OutputDropZone = styled.div`
  min-height: 120px;
  border: 2px dashed ${(p) => (p.$isOver ? '#0c53bb' : '#c9d2de')};
  background: ${(p) => (p.$isOver ? '#f1f6fe' : 'transparent')};
  border-radius: 8px;
  padding: 8px;
`;

export const SegmentCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid #dde2e9;
  background: #ffffff;
  border-radius: 8px;
`;

export const SegmentIndex = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #8a97a8;
  padding-top: 2px;
  min-width: 18px;
`;

export const SegmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SegmentName = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SegmentPages = styled.div`
  font-size: 11px;
  color: #48566b;
  margin-top: 2px;
`;

export const SegmentLabelInput = styled.input`
  width: 100%;
  margin-top: 6px;
  padding: 4px 8px;
  border: 1px solid #e3e8ef;
  border-radius: 4px;
  font-family: inherit;
  font-size: 11px;
  color: #48566b;

  &:focus {
    outline: none;
    border-color: #0c53bb;
  }
`;

export const BatesCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
  color: #48566b;
  cursor: pointer;
  user-select: none;

  input {
    margin: 0;
    cursor: pointer;
  }
`;

export const DropIndicator = styled.div`
  height: 3px;
  border-radius: 2px;
  background: #0c53bb;
  margin: 2px 0 6px;
`;

export const ZoomControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

export const SegmentButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #5a6c87;
  display: inline-flex;

  &:hover {
    background: #f1f6fe;
  }
`;

export const DragHandleButton = styled(SegmentButton)`
  cursor: grab;
`;

export const OutputSummary = styled.div`
  padding: 10px 16px;
  border-top: 1px solid #eef1f6;
  font-size: 12px;
  color: #48566b;
  flex-shrink: 0;
`;

export const DragBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #0c53bb;
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  padding: 8px 12px;
  box-shadow: 0 4px 12px rgba(12, 83, 187, 0.35);
`;
